import { DATE_TIME_FORMAT_API } from '@shared/utils'
import { UploadChangeParam, UploadFile } from 'antd/es/upload'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'

type Media = {
  name?: string | null
  file: {
    url: string
    headers: Record<string, string | null>
  }
}

export const downloadFile = async (record: Media | Blob, name?: string) => {
  try {
    const blob = record instanceof Blob ? record : await getBlobFromUrl(record)

    if (!blob) return

    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = name || record.name || 'file'
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

export const getBlobFromUrl = async (record: Media) => {
  try {
    const { url, headers } = record.file

    const fetchHeaders = headers as Record<string, string>

    const response = await fetch(url, { headers: fetchHeaders })
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`)
    }
    const blob = await response.blob()

    return blob
  } catch (error) {
    console.error('Error transform to blob:', error)
    return undefined
  }
}

export const getFileName = (name: string, mimeType: string) =>
  `${name}_${dayjs().format(DATE_TIME_FORMAT_API)}.${mimeType}`

export enum EXPORT_TYPE {
  XLSX = 'xlsx',
  CSV = 'csv'
}

export const validatePdfFile = (
  value: UploadChangeParam<UploadFile>,
  t: TFunction
) => {
  if (!value) return Promise.resolve()

  if (value?.file?.url) {
    return Promise.resolve()
  }

  const file = value?.file || value

  const isValidPdf =
    file?.type === 'application/pdf' ||
    file?.name?.toLowerCase().endsWith('.pdf')

  if (!isValidPdf) {
    return Promise.reject(new Error(t('error_invalid_file_type')))
  }

  return Promise.resolve()
}

export const validateFileType = (
  value: UploadChangeParam<UploadFile>,
  message: string,
  validTypes: string[],
  validExtensions: string[]
) => {
  if (!value || !value?.fileList?.length) return Promise.resolve()

  if (value?.file?.url) {
    return Promise.resolve()
  }
  const file = value?.file || value

  const isValidPdf =
    validTypes.includes(file?.type || '') ||
    validExtensions.some((ext) => file?.name?.toLowerCase().endsWith(ext))

  if (!isValidPdf) {
    return Promise.reject(new Error(message))
  }

  return Promise.resolve()
}

export const blobToBase64 = (
  blob: Blob
): Promise<{ base64: string; pureBase64: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader?.result as string
      resolve({ base64: result, pureBase64: result?.split(',')?.[1] })
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
